import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Character, Element } from '../types';
import { charactersAPI } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import imageLoader from '../utils/optimizedImageLoader';

import { useConfirmModal } from '../hooks/useConfirmModal';
import { useOptimizedImage } from '../hooks/useOptimizedImage';
import ConfirmModal from './ConfirmModal';
import UploadImageModal from './UploadImageModal';
import SidePanel from './SidePanel';
import OptimizedImage from './OptimizedImage';
import './CharacterCard.css';

// Create Template Form Component
interface CreateTemplateFormProps {
  field: 'features' | 'attributes' | 'summary';
  currentContent: string;
  onCreateTemplate: (name: string, content: string) => void;
  onCancel: () => void;
}

const CreateTemplateForm: React.FC<CreateTemplateFormProps> = ({
  field,
  currentContent,
  onCreateTemplate,
  onCancel
}) => {
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState(currentContent);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateName.trim() || !templateContent.trim()) return;
    
    setLoading(true);
    onCreateTemplate(templateName.trim(), templateContent.trim());
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="create-template-form">
      <div className="form-group">
        <label htmlFor="template-name">Template Name</label>
        <input
          id="template-name"
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter template name..."
          required
          autoFocus
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="template-content">Template Content</label>
        <textarea
          id="template-content"
          value={templateContent}
          onChange={(e) => setTemplateContent(e.target.value)}
          placeholder="Enter template content..."
          required
          rows={10}
        />
        <small className="form-help">
          This will be the content that gets added when the template is selected.
        </small>
      </div>
      
      <div className="form-actions">
        <button type="button" className="btn btn--gray" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn--gold" disabled={loading || !templateName.trim() || !templateContent.trim()}>
          {loading ? 'Creating...' : 'Create Template'}
        </button>
      </div>
    </form>
  );
};

// Extend Window interface for global functions
declare global {
  interface Window {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }
}

interface CharacterCardProps {
  character: Character;
  onUpdate: (character: Character) => void;
  onDelete: (characterId: string) => void;
  onOpenSidePanel: (character: Character, field: string) => void;
  onOpenPreviewPanel: (elementIds: string[]) => void;
  defaultTab?: string;
}

const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  onUpdate,
  onDelete,
  onOpenSidePanel,
  onOpenPreviewPanel,
  defaultTab = 'summary'
}) => {
  // Debug logging for character data
  useEffect(() => {
    console.log('🔄 CharacterCard rendering for:', {
      id: character._id,
      name: character.name,
      abilities: character.abilities?.length || 0,
      weapons: character.weapons?.length || 0,
      armor: character.armor?.length || 0,
      items: character.items?.length || 0,
      species: character.species?.length || 0,
      gender: character.gender?.length || 0,
      beliefs: character.beliefs?.length || 0,
      family: character.family?.length || 0,
      affiliations: character.affiliations?.length || 0
    });
  }, [character._id]);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showSaveCancel, setShowSaveCancel] = useState(false);

  // Debug logging for editing state
  useEffect(() => {
    console.log('🔄 Editing state changed:', { editingField, editValue, showSaveCancel });
  }, [editingField, editValue, showSaveCancel]);
  const [loading, setLoading] = useState(false);
  const [showDeleteBtn, setShowDeleteBtn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [sidePanelField, setSidePanelField] = useState('');
  const [sidePanelTitle, setSidePanelTitle] = useState('');
  const [showTextEditModal, setShowTextEditModal] = useState(false);
  const [textEditData, setTextEditData] = useState<{
    field: string;
    title: string;
    content: string;
  } | null>(null);
  const [showTemplateMenu, setShowTemplateMenu] = useState<'features' | 'attributes' | 'summary' | null>(null);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [customTemplates, setCustomTemplates] = useState<{
    features: Array<{ name: string; content: string }>;
    attributes: Array<{ name: string; content: string }>;
    summary: Array<{ name: string; content: string }>;
  }>(() => {
    const saved = localStorage.getItem('aethermap-custom-templates');
    return saved ? JSON.parse(saved) : { features: [], attributes: [], summary: [] };
  });

  const [activeTab, setActiveTab] = useState(() => {
    // Try to get the stored tab for this character
    const storedTab = localStorage.getItem(`character-tab-${character._id}`);
    const validTabs = ['summary', 'features', 'attributes', 'equipment', 'threads'];
    const tab = storedTab ?? defaultTab;
    return validTabs.includes(tab) ? tab : defaultTab;
  });

  // Listen for custom template changes from other character cards
  useEffect(() => {
    let isHandlingEvent = false;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'aethermap-custom-templates' && e.newValue && !isHandlingEvent) {
        try {
          const newTemplates = JSON.parse(e.newValue);
          setCustomTemplates(newTemplates);
        } catch (error) {
          console.error('Error parsing custom templates:', error);
        }
      }
    };

    const handleCustomTemplateChange = () => {
      if (isHandlingEvent) return; // Prevent duplicate handling
      
      isHandlingEvent = true;
      // Reload custom templates from localStorage
      const saved = localStorage.getItem('aethermap-custom-templates');
      if (saved) {
        try {
          const newTemplates = JSON.parse(saved);
          setCustomTemplates(newTemplates);
        } catch (error) {
          console.error('Error parsing custom templates:', error);
        }
      }
      
      // Reset flag after a short delay
      setTimeout(() => {
        isHandlingEvent = false;
      }, 100);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('custom-template-change', handleCustomTemplateChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('custom-template-change', handleCustomTemplateChange);
    };
  }, []);

  // Refresh custom templates when text edit modal opens
  useEffect(() => {
    if (showTextEditModal && textEditData) {
      refreshCustomTemplates();
    }
  }, [showTextEditModal, textEditData]);

  // Remove the local cardRef
  // const cardRef = useRef<HTMLDivElement>(null);
  
  // Card loading hook (now using useOptimizedImage with overlay)
  const imageSrc = getImageUrl(character.imageUrl, character.image, character._id, imageKey);
  const {
    cardRef,
    overlayRef,
    loadingState = { isLoading: false, progress: 0, error: false }
  } = useOptimizedImage({
    src: imageSrc,
    alt: character.name,
    withCardOverlay: true
  });
  
  const { modalState, showConfirm, handleConfirm, handleCancel: handleModalCancel } = useConfirmModal();

  // Tab switching is now handled by React state
  // Removed DOM-based initialization

  // Store active tab in localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`character-tab-${character._id}`, activeTab);
  }, [activeTab, character._id]);

  // Update activeTab when defaultTab prop changes
  useEffect(() => {
    const validTabs = ['summary', 'features', 'attributes', 'equipment', 'threads'];
    if (validTabs.includes(defaultTab)) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);



  // Monitor character changes for debugging (only log when imageUrl changes)
  useEffect(() => {
    if (character.imageUrl && character.imageUrl.includes('amazonaws.com')) {
      console.log('🔄 Character image updated:', {
        id: character._id,
        name: character.name,
        imageUrl: character.imageUrl.substring(0, 50) + '...'
      });
    }
  }, [character.imageUrl]);

  const handleEdit = (field: string, value: string) => {
    console.log('🔄 handleEdit called:', { field, value });
    setEditingField(field);
    setEditValue(value);
  };

  const handleTextClick = (field: string, element: HTMLElement) => {
    if (editingField !== field) {
      console.log('🔄 handleTextClick called:', { field });
      setEditingField(field);
      setEditValue(element.textContent || '');
    }
  };

  const handleSave = async () => {
    if (!editingField) return;

    try {
      setLoading(true);
      console.log('🔄 Saving character field:', { field: editingField, value: editValue });
      console.log('🔄 Original character image:', character.image);
      
      const updatedCharacter = await charactersAPI.update(character._id, {
        [editingField]: editValue
      });
      
      console.log('✅ Character updated successfully:', updatedCharacter);
      console.log('🔄 Updated character image:', updatedCharacter.image);
      console.log('🔄 Updated character imageUrl:', updatedCharacter.imageUrl);
      
      // If the backend didn't return an imageUrl, preserve the existing one
      const finalCharacter = {
        ...updatedCharacter,
        imageUrl: updatedCharacter.imageUrl || character.imageUrl
      };
      
      console.log('🔄 Final character with preserved imageUrl:', finalCharacter.imageUrl);
      
      onUpdate(finalCharacter);
      if (editingField === 'article') {
        setEditValue(finalCharacter.article || '');
      }
      setEditingField(null);
      setShowSaveCancel(false);
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast('Character updated successfully!', 'success');
      }
    } catch (error) {
      console.error('❌ Failed to update character:', error);
      
      // Show error toast
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update character. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setShowSaveCancel(false);
  };

  const handleDelete = async () => {
    // Use React confirm modal
    const confirmDelete = await showConfirm('Are you sure you want to delete this character?', { type: 'warning' });
    if (!confirmDelete) return;
    onDelete(character._id);
  };

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please select an image file.', 'error');
      }
      return;
    }

    try {
      setUploading(true);
      
      // Create FormData
      const formData = new FormData();
      formData.append('image', file);
      formData.append('cardType', 'characters');
      
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/characters/${character._id}/image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Upload successful:', data);
      
      // Update the character with new image data
      console.log('🔄 Server response data:', data);
      
      // Extract the new image URL from the correct response path
      const newImageUrl = data.data?.imageUrl || data.data?.character?.imageUrl;
      const uploadTimestamp = data.data?.uploadTimestamp || Date.now();
      const newImage = data.data?.character?.image;
      
      console.log('🔄 Extracted data:', { newImageUrl, uploadTimestamp, newImage });
      
      if (newImageUrl) {
        console.log('🔄 Updating character with new image URL:', newImageUrl);
        const updatedCharacter = { 
          ...character, 
          imageUrl: newImageUrl,
          image: newImage || character.image, // Also update the image key
          uploadTimestamp: uploadTimestamp // Include upload timestamp
        };
        onUpdate(updatedCharacter);
        
        // Force image reload by generating new key
        const newKey = Date.now();
        console.log('🔄 Generating new image key:', newKey);
        setImageKey(newKey);
        setImageLoading(true);
        
        // Update the optimized image loader
        imageLoader.updateImage(character.imageUrl || '', newImageUrl);
      } else {
        console.error('❌ No image URL in server response');
        console.error('❌ Response structure:', data);
      }
      
      if (typeof window.showToast === 'function') {
        window.showToast('Image updated successfully!', 'success');
      }
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update image. Please try again.', 'error');
      }
      throw error; // Re-throw for modal to handle
    } finally {
      setUploading(false);
    }
  };

  const handleImageClick = () => {
    setDroppedFile(null);
    setShowUploadModal(true);
  };

  const handleOpenSidePanel = (field: string, title: string) => {
    console.log('🔄 Opening side panel:', { field, title });
    setSidePanelField(field);
    setSidePanelTitle(title);
    setShowSidePanel(true);
    console.log('🔄 Side panel state set to true');
  };

  const handleSidePanelClose = () => {
    setShowSidePanel(false);
    setSidePanelField('');
    setSidePanelTitle('');
  };

  const handleOpenTextEditModal = (field: string, title: string, content: string) => {
    console.log('🔄 Opening text edit modal:', { field, title, content });
    setTextEditData({
      field,
      title,
      content
    });
    setShowTextEditModal(true);
  };

  const handleTextEditModalClose = () => {
    setShowTextEditModal(false);
    setTextEditData(null);
  };

  const handleTextEditSave = async () => {
    if (!textEditData) return;

    try {
      setLoading(true);
      console.log('🔄 Saving text field:', { field: textEditData.field, value: textEditData.content });
      
      // Map 'summary' field back to 'article' for database
      const dbField = textEditData.field === 'summary' ? 'article' : textEditData.field;
      
      const updatedCharacter = await charactersAPI.update(character._id, {
        [dbField]: textEditData.content
      });
      
      console.log('✅ Character updated successfully:', updatedCharacter);
      
      onUpdate(updatedCharacter);
      handleTextEditModalClose();
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast('Text updated successfully!', 'success');
      }
    } catch (error) {
      console.error('❌ Failed to update text:', error);
      
      // Show error toast
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update text. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModalTemplateSelect = (template: { name: string; content: string }) => {
    if (!textEditData) return;
    
    const currentContent = textEditData.content;
    const newContent = currentContent ? `${currentContent}\n\n${template.content}` : template.content;
    
    setTextEditData({
      ...textEditData,
      content: newContent
    });
  };

  const handleCreateTemplate = (templateName: string, templateContent: string) => {
    if (!textEditData) return;
    
    // Map the field to the correct key in customTemplates
    const fieldKey = textEditData.field === 'features' || textEditData.field === 'attributes' || textEditData.field === 'summary'
      ? textEditData.field 
      : 'features'; // fallback to features if field is not recognized
    
    const newTemplate = { name: templateName, content: templateContent };
    
    setCustomTemplates(prev => {
      // Ensure the field exists and is an array
      const currentFieldTemplates = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
      
      // Check if template with same name already exists
      const templateExists = currentFieldTemplates.some(t => t.name === templateName);
      if (templateExists) {
        console.warn('Template with this name already exists:', templateName);
        return prev; // Don't update if template already exists
      }
      
      const updated = {
        ...prev,
        [fieldKey]: [...currentFieldTemplates, newTemplate]
      };
      localStorage.setItem('aethermap-custom-templates', JSON.stringify(updated));
      
      // Dispatch custom event to notify other character cards
      window.dispatchEvent(new CustomEvent('custom-template-change', {
        detail: { action: 'create', template: newTemplate, field: fieldKey }
      }));
      
      return updated;
    });
    
    setShowCreateTemplateModal(false);
    
    if (typeof window.showToast === 'function') {
      window.showToast('Custom template created successfully!', 'success');
    }
  };

  const handleDeleteCustomTemplate = (field: 'features' | 'attributes' | 'summary', templateName: string) => {
    // Map the field to the correct key in customTemplates
    const fieldKey = field === 'features' || field === 'attributes' || field === 'summary' ? field : 'features';
    
    setCustomTemplates(prev => {
      // Ensure the field exists and is an array
      const currentFieldTemplates = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
      
      const updated = {
        ...prev,
        [fieldKey]: currentFieldTemplates.filter(t => t.name !== templateName)
      };
      localStorage.setItem('aethermap-custom-templates', JSON.stringify(updated));
      
      // Dispatch custom event to notify other character cards
      window.dispatchEvent(new CustomEvent('custom-template-change', {
        detail: { action: 'delete', templateName, field: fieldKey }
      }));
      
      return updated;
    });
    
    if (typeof window.showToast === 'function') {
      window.showToast('Template deleted successfully!', 'success');
    }
  };

  const refreshCustomTemplates = () => {
    const saved = localStorage.getItem('aethermap-custom-templates');
    if (saved) {
      try {
        const newTemplates = JSON.parse(saved);
        // Deduplicate templates before setting state
        const deduplicatedTemplates = {
          features: deduplicateTemplates(newTemplates.features || []),
          attributes: deduplicateTemplates(newTemplates.attributes || []),
          summary: deduplicateTemplates(newTemplates.summary || [])
        };
        setCustomTemplates(deduplicatedTemplates);
        
        // Update localStorage with deduplicated data if there were duplicates
        if (JSON.stringify(newTemplates) !== JSON.stringify(deduplicatedTemplates)) {
          localStorage.setItem('aethermap-custom-templates', JSON.stringify(deduplicatedTemplates));
        }
      } catch (error) {
        console.error('Error parsing custom templates:', error);
      }
    }
  };

  const deduplicateTemplates = (templates: Array<{ name: string; content: string }>) => {
    const seen = new Set();
    return templates.filter(template => {
      if (seen.has(template.name)) {
        return false; // Remove duplicate
      }
      seen.add(template.name);
      return true; // Keep first occurrence
    });
  };

  const handleTabSwitch = (tabName: string) => {
    setActiveTab(tabName);
  };

  // Template data
  const featuresTemplates = [
    {
      name: 'Basic Stats',
      content: 'HP:\nMP:\nATK:\nDEF:\nSPD:\nMAG:'
    },
    {
      name: 'Combat Stats',
      content: 'Attack Power:\nDefense:\nSpeed:\nCritical Rate:\nCritical Damage:\nEvasion:'
    },
    {
      name: 'Magic Stats',
      content: 'Magic Power:\nMagic Defense:\nMP Recovery:\nSpell Casting Speed:\nElemental Affinity:\nMana Pool:'
    },
    {
      name: 'Character Stats',
      content: 'Strength:\nDexterity:\nConstitution:\nIntelligence:\nWisdom:\nCharisma:'
    }
  ];

  const attributesTemplates = [
    {
      name: 'Physical Traits',
      content: 'Height:\nWeight:\nEye Color:\nHair Color:\nSkin Tone:\nBuild:'
    },
    {
      name: 'Personal Details',
      content: 'Age:\nBirth Date:\nBirth Place:\nNationality:\nOccupation:\nMarital Status:'
    },
    {
      name: 'Appearance',
      content: 'Height:\nWeight:\nEye Color:\nHair Color:\nHair Style:\nDistinguishing Features:'
    },
    {
      name: 'Character Details',
      content: 'Age:\nHeight:\nWeight:\nEye Color:\nHair Color:\nVoice Type:\nScars/Marks:'
    }
  ];

  const summaryTemplates = [
    {
      name: 'Hero Summary',
      content: 'A courageous warrior who has dedicated their life to protecting the innocent. Known for their unwavering determination and strong moral compass. Despite facing countless challenges, they continue to fight for justice and hope.'
    },
    {
      name: 'Villain Summary',
      content: 'A cunning and ruthless antagonist whose dark past has shaped them into a formidable adversary. Driven by revenge and power, they will stop at nothing to achieve their goals, leaving destruction in their wake.'
    },
    {
      name: 'Anti-Hero Summary',
      content: 'A complex character who walks the line between good and evil. While they may have noble intentions, their methods are often questionable. Their troubled past has made them distrustful, but they still fight for what they believe is right.'
    },
    {
      name: 'Mentor Summary',
      content: 'A wise and experienced individual who guides others on their journey. Having learned from their own mistakes, they now share their knowledge and wisdom to help the next generation avoid the same pitfalls.'
    },
    {
      name: 'Companion Summary',
      content: 'A loyal friend and ally who provides support and companionship on the journey. While they may not be the strongest fighter, their unwavering loyalty and unique skills make them an invaluable member of the team.'
    },
    {
      name: 'Mysterious Character',
      content: 'A enigmatic figure whose true nature and motives remain shrouded in mystery. Their past is unknown, and their intentions unclear. They seem to know more than they let on, but whether they are friend or foe remains to be seen.'
    },
    {
      name: 'Tragic Hero',
      content: 'A character whose life has been marked by loss and tragedy. Despite their suffering, they continue to fight for a better future. Their pain has made them stronger, but also more vulnerable to darkness.'
    },
    {
      name: 'Rising Star',
      content: 'A young and promising individual who is just beginning to discover their true potential. Though inexperienced, their natural talent and determination suggest they will become a force to be reckoned with.'
    }
  ];

  const handleTemplateSelect = (field: 'features' | 'attributes' | 'summary', template: { name: string; content: string }) => {
    // Map 'summary' field to 'article' for database
    const dbField = field === 'summary' ? 'article' : field;
    
    setEditingField(dbField);
    
    // Get current content and append template
    const currentContent = (character as any)[dbField] || '';
    const newContent = currentContent 
      ? `${currentContent}\n\n${template.content}` 
      : template.content;
    
    setEditValue(newContent);
    setShowSaveCancel(true);
    setShowTemplateMenu(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    
    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please drop an image file.', 'error');
      }
      return;
    }
    
    // Set the dropped file and open upload modal
    setDroppedFile(file);
    setShowUploadModal(true);
  };

  const renderArrayField = (field: string, items: Element[], title: string) => {
    // For single selection fields, show only the first element
    const singleSelectionFields = ['species', 'gender', 'beliefs', 'family', 'affiliations'];
    const isSingleSelection = singleSelectionFields.includes(field);
    
    const displayItems = isSingleSelection && items.length > 0 ? [items[0]] : items;
    
    // Debug logging for all fields
    console.log(`🔄 renderArrayField for ${field}:`, {
      items: items,
      displayItems: displayItems,
      isSingleSelection: isSingleSelection,
      itemsLength: items.length,
      firstItem: items[0] || null
    });
    
    return (
      <div className="card-array__container">
        <button
          className="card-array__search-btn"
          type="button"
          aria-label={`Update ${title}`}
          onClick={() => {
            console.log('🔄 Button clicked:', { field, title });
            handleOpenSidePanel(field, title);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="darkorchid">
            <use xlinkHref="/img/icons.svg#icon-edit" />
          </svg>
          <span className="card-array__badge">{items.length}</span>
        </button>
        <div className="card-array__title-container">
          <h4 className="card-array__title--small">{title}</h4>
          {displayItems && displayItems.length > 0 ? (
            <button
              className="element-preview-btn"
              type="button"
              onClick={() => onOpenPreviewPanel(displayItems.map((item: Element) => item._id))}
              title={displayItems[0].name || 'Unnamed Element'}
            >
              {displayItems[0].name || 'Unnamed Element'}
            </button>
          ) : (
            <span className="no-elements">NONE</span>
          )}
        </div>
      </div>
    );
  };

  // Add ref for summary field
  const summaryRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div 
        className="character-card" 
        data-character-id={character._id}
        ref={cardRef}
        onMouseEnter={() => setShowDeleteBtn(true)}
        onMouseLeave={() => setShowDeleteBtn(false)}
        style={{ position: 'relative' }}
      >
        {/* Card loading overlay */}
        <div ref={overlayRef} />
      <button
        className="card__delete-btn"
        type="button"
        onClick={handleDelete}
        style={{ display: showDeleteBtn ? 'block' : 'none' }}
      >
        ×
      </button>

      <div className="card__header">
        <div 
          className="card__picture" 
          style={{ 
            position: 'relative', 
            cursor: 'pointer', 
            transition: 'all 0.3s ease',
            border: isDragging ? '3px dashed #55c57a' : '',
            backgroundColor: isDragging ? 'rgba(85, 197, 122, 0.1)' : ''
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          title={`Drag and drop an image here to replace ${character.name}'s picture`}
        >
          <OptimizedImage
            src={getImageUrl(character.imageUrl, character.image, character._id, imageKey)}
            alt={character.name}
            className="card__picture-img"
            style={{ 
              opacity: uploading || imageLoading ? 0.5 : 1,
              filter: uploading || imageLoading ? 'grayscale(50%)' : 'none',
              cursor: 'pointer'
            }}
            onClick={handleImageClick}
            onLoad={() => {
              console.log('✅ Image loaded successfully');
              setImageLoading(false);
            }}
            onError={() => {
              console.error('❌ Image failed to load');
              setImageLoading(false);
            }}
            dataAttributes={{
              'data-image-key': character.image || '',
              'data-character-id': character._id
            }}
            onDragOver={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
            onDragLeave={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
            onDrop={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
          />
          {(loadingState?.isLoading ?? false) && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, width: '100%', height: '100%',
              background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.75) 0%, rgba(22, 33, 62, 0.75) 50%, rgba(15, 52, 96, 0.75) 100%)',
              zIndex: 99999,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2.5px solid',
              borderImage: 'linear-gradient(90deg, #7fbcff 0%, #b388ff 100%) 1',
              borderRadius: 'inherit',
              backdropFilter: 'blur(2px)'
            }}>
              <img src="/img/AeLogo.ico" alt="Loading..." style={{ width: 50, height: 50, marginBottom: 16, animation: 'cardBounce 1.2s ease-in-out infinite, cardGlow 2s ease-in-out infinite' }} />
              <span style={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.7)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}>Loading...</span>
            </div>
          )}
          <div 
            className="drag-overlay"
            style={{ 
              opacity: isDragging ? 1 : 0,
              borderColor: isDragging ? '#55c57a' : 'transparent'
            }}
          >
            <span style={{ color: '#55c57a', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Drop image here
            </span>
          </div>
          <span 
            className="card__img-success" 
            style={{ 
              display: uploading ? 'block' : 'none',
              opacity: uploading ? 1 : 0
            }}
          >
            ✓
          </span>
        </div>

        <h3 className="heading-tertirary card__name-container">
          {editingField === 'name' ? (
            <>
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="card__name-edit"
                autoFocus
                onFocus={(e) => {
                  // Position cursor at the end
                  e.target.setSelectionRange(e.target.value.length, e.target.value.length);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSave();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancel();
                  }
                }}
              />
              <div className="edit-buttons">
                  <button
                  className="btn btn--gold btn--xs save-btn"
                    onClick={handleSave}
                    disabled={loading}
                  >
                  {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="btn btn--gray btn--xs cancel-btn"
                    onClick={handleCancel}
                  disabled={loading}
                  >
                    Cancel
                  </button>
              </div>
            </>
          ) : (
            <span
              className="card__name"
              data-character-id={character._id}
              data-original={character.name}
              data-field="name"
              onClick={() => {
                setEditingField('name');
                setEditValue(character.name);
              }}
            >
              {character.name}
            </span>
          )}
        </h3>
      </div>

      <div className="card__details">
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabSwitch('summary')}
          >
            Summary
          </button>
          <button
            className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabSwitch('features')}
          >
            Features
          </button>
          <button
            className={`tab-btn ${activeTab === 'attributes' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabSwitch('attributes')}
          >
            Attributes
          </button>
          <button
            className={`tab-btn ${activeTab === 'equipment' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabSwitch('equipment')}
          >
            Equipment
          </button>
          <button
            className={`tab-btn btn--red ${activeTab === 'threads' ? 'active' : ''}`}
            type="button"
            onClick={() => handleTabSwitch('threads')}
          >
            Threads
          </button>
        </div>

        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="tab-content" id="tab-summary">
            <div className="tab-summary-flex-row">
              <div className="left-content">
                <div className="card__text-container">
                  <div className="summary-header">
                    <h4 className="card__sub-heading">Summary</h4>
                    <div className="header-buttons">
                      {editingField === 'article' && showSaveCancel && (
                        <>
                          <div className="inline-edit-buttons">
                            <button
                              className="btn btn--gold btn--xs save-btn"
                              onClick={handleSave}
                              disabled={loading}
                            >
                              {loading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className="btn btn--gray btn--xs cancel-btn"
                              onClick={handleCancel}
                              disabled={loading}
                            >
                              Cancel
                            </button>
                          </div>
                          <button
                            className="text-edit-modal-btn"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => handleOpenTextEditModal('summary', 'Summary', character.article || '')}
                            title="Full Edit (modal)"
                            style={{ marginTop: 8 }}
                          >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                              <use xlinkHref="/img/icons.svg#icon-edit" />
                            </svg>
                            Full Edit
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    ref={summaryRef}
                    className={`card__text${editingField === 'article' ? ' editing' : ''}`}
                    contentEditable={editingField === 'article'}
                    suppressContentEditableWarning
                    data-original={character.article}
                    data-field="article"
                    onClick={e => {
                      if (editingField !== 'article') {
                        setEditingField('article');
                        setShowSaveCancel(true);
                        setTimeout(() => summaryRef.current?.focus(), 0);
                      }
                    }}
                    onBlur={e => {
                      if (editingField === 'article') {
                        const newValue = e.currentTarget.textContent || '';
                        if (newValue !== character.article) {
                          setEditValue(newValue);
                          setShowSaveCancel(true);
                        } else {
                          setEditingField(null);
                          setShowSaveCancel(false);
                        }
                      }
                    }}
                    onKeyDown={e => {
                      if (editingField === 'article') {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          const newValue = (e.target as HTMLElement).textContent || '';
                          if (newValue !== character.article) {
                            setEditValue(newValue);
                            handleSave();
                          } else {
                            setEditingField(null);
                            setShowSaveCancel(false);
                          }
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingField(null);
                          setShowSaveCancel(false);
                        }
                      }
                    }}
                    style={{ whiteSpace: 'pre-line', cursor: 'pointer' }}
                  >
                    {character.article || 'No description available. Click to edit.'}
                  </div>
                </div>
              </div>
              <div className="right-content">
                                  <div className="array-fields-container">
                    {renderArrayField('species', character.species || [], 'Species')}
                    {renderArrayField('gender', character.gender || [], 'Gender')}
                    {renderArrayField('beliefs', character.beliefs || [], 'Beliefs')}
                    {renderArrayField('family', character.family || [], 'Family')}
                    {renderArrayField('affiliations', character.affiliations || [], 'Affiliations')}
                  </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Tab */}
        <div className={`tab-content ${activeTab === 'features' ? '' : 'hidden'}`} id="tab-features">
            <div className="card__text-container">
              <div className="features-header">
                <h4 className="card__sub-heading">Features</h4>
                <div className="header-buttons">
                                  {editingField !== 'features' && (
                  <button
                    className="text-edit-modal-btn"
                    onClick={() => setShowTemplateMenu('features')}
                    title="Choose a template"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <use xlinkHref="/img/icons.svg#icon-clipboard" />
                    </svg>
                    Templates
                  </button>
                )}
                  <button
                    className="text-edit-modal-btn"
                    onClick={() => handleOpenTextEditModal('features', 'Features', character.features || '')}
                    title="Edit Features in full screen"
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    Full Edit
                  </button>
                </div>
              </div>
              
              {showTemplateMenu === 'features' && (
                <div className="template-menu">
                  <div className="template-menu-header">
                    <h5>Choose a Features Template</h5>
                    <button 
                      className="template-close-btn"
                      onClick={() => setShowTemplateMenu(null)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="template-options">
                    {featuresTemplates.map((template) => (
                      <button
                        key={`features-${template.name}`}
                        className="template-option"
                        onClick={() => handleTemplateSelect('features', template)}
                      >
                        <span className="template-name">{template.name}</span>
                        <span className="template-preview">{template.content.split('\n').slice(0, 3).join(', ')}...</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div
                className={`card__text ${editingField === 'features' ? 'editing' : ''}`}
                contentEditable
                suppressContentEditableWarning
                data-original={character.features}
                data-field="features"
                onClick={(e) => handleTextClick('features', e.currentTarget)}
                onBlur={(e) => {
                  // Only save if we're not currently editing this field
                  if (editingField !== 'features') {
                    const newValue = e.currentTarget.textContent || '';
                    if (newValue !== character.features) {
                      handleEdit('features', newValue);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                style={{ whiteSpace: 'pre-line' }}
              >
                {character.features || 'No features available.'}
              </div>
              
              {editingField === 'features' && (
                <div className="inline-edit-buttons">
                  <button
                    className="btn btn--gold btn--xs save-btn"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="btn btn--gray btn--xs cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

        {/* Attributes Tab */}
        <div className={`tab-content ${activeTab === 'attributes' ? '' : 'hidden'}`} id="tab-attributes">
            <div className="tab-summary-flex-row">
              <div className="left-content">
                <div className="card__text-container">
                  <div className="attributes-header">
                    <h4 className="card__sub-heading">Attributes</h4>
                    <div className="header-buttons">
                      {editingField !== 'attributes' && (
                        <button
                          className="text-edit-modal-btn"
                          onClick={() => setShowTemplateMenu('attributes')}
                          title="Choose a template"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <use xlinkHref="/img/icons.svg#icon-clipboard" />
                          </svg>                          
                        </button>
                      )}
                      <button
                        className="text-edit-modal-btn"
                        onClick={() => handleOpenTextEditModal('attributes', 'Attributes', character.attributes || '')}
                        title="Edit Attributes in full screen"
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <use xlinkHref="/img/icons.svg#icon-edit" />
                        </svg>                        
                      </button>
                    </div>
                  </div>
                  
                  {showTemplateMenu === 'attributes' && (
                    <div className="template-menu">
                      <div className="template-menu-header">
                        <h5>Choose an Attributes Template</h5>
                        <button 
                          className="template-close-btn"
                          onClick={() => setShowTemplateMenu(null)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="template-options">
                        {attributesTemplates.map((template) => (
                          <button
                            key={`attributes-${template.name}`}
                            className="template-option"
                            onClick={() => handleTemplateSelect('attributes', template)}
                          >
                            <span className="template-name">{template.name}</span>
                            <span className="template-preview">{template.content.split('\n').slice(0, 3).join(', ')}...</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    className={`card__text ${editingField === 'attributes' ? 'editing' : ''}`}
                    contentEditable
                    suppressContentEditableWarning
                    data-original={character.attributes}
                    data-field="attributes"
                    onClick={(e) => handleTextClick('attributes', e.currentTarget)}
                    onBlur={(e) => {
                      // Only save if we're not currently editing this field
                      if (editingField !== 'attributes') {
                        const newValue = e.currentTarget.textContent || '';
                        if (newValue !== character.attributes) {
                          handleEdit('attributes', newValue);
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {character.attributes || 'No attributes available.'}
                  </div>
                  
                  {editingField === 'attributes' && (
                    <div className="inline-edit-buttons">
                      <button
                        className="btn btn--blue btn--xs save-btn"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn--gray btn--xs cancel-btn"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="right-content">
                {/* Abilities Column */}
                <div className="abilities-column">
                  <div className="card-array__container">
                    <button
                      className="card-array__search-btn"
                      type="button"
                      aria-label="Update Abilities"
                      onClick={() => handleOpenSidePanel('abilities', 'Abilities')}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="darkorchid">
                        <use xlinkHref="/img/icons.svg#icon-edit" />
                      </svg>
                      <span className="card-array__badge">{(character.abilitiesDisplayed || character.abilities || []).length}</span>
                    </button>
                    <h3 className="card-array__title">Abilities</h3>
                  </div>
                  <div className="abilities-category">
                    <ul className="button-list">
                      {(character.abilitiesDisplayed || character.abilities || []).map((ability) => {
                        console.log('🔄 Rendering ability:', ability);
                        return (
                          <li key={ability._id || `ability-${ability.name}`}>
                            <Link className="nav__el" to={`/element/${ability.slug}`}>
                              {ability.name || 'Unnamed Ability'}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Equipment Tab */}
        <div className={`tab-content ${activeTab === 'equipment' ? '' : 'hidden'}`} id="tab-equipment">
            <div className="tab-summary-flex-equip">
              {/* Weapons Column */}
              <div className="equipment-column">
                <div className="card-array__container">
                  <button
                    className="card-array__search-btn"
                    type="button"
                    aria-label="Update Weapons"
                    onClick={() => handleOpenSidePanel('weapons', 'Weapons')}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="darkorchid">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    <span className="card-array__badge">{(character.weaponsDisplayed || character.weapons || []).length}</span>
                  </button>
                  <h3 className="card-array__title">Weapons</h3>
                </div>
                <div className="equipment-category">
                  <ul className="button-list">
                    {(character.weaponsDisplayed || character.weapons || []).map(weapon => {
                      console.log('🔄 Rendering weapon:', weapon);
                      return (
                        <li key={weapon._id || `weapon-${weapon.name}`}>
                          <Link className="nav__el" to={`/element/${weapon.slug}`}>
                            {weapon.name || 'Unnamed Weapon'}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Armor Column */}
              <div className="equipment-column">
                <div className="card-array__container">
                  <button
                    className="card-array__search-btn"
                    type="button"
                    aria-label="Update Armor"
                    onClick={() => handleOpenSidePanel('armor', 'Armor')}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="darkorchid">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    <span className="card-array__badge">{(character.armorDisplayed || character.armor || []).length}</span>
                  </button>
                  <h3 className="card-array__title">Armor</h3>
                </div>
                <div className="equipment-category">
                  <ul className="button-list">
                    {(character.armorDisplayed || character.armor || []).map(armor => {
                      console.log('🔄 Rendering armor:', armor);
                      return (
                        <li key={armor._id || `armor-${armor.name}`}>
                          <Link className="nav__el" to={`/element/${armor.slug}`}>
                            {armor.name || 'Unnamed Armor'}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Items Column */}
              <div className="equipment-column">
                <div className="card-array__container">
                  <button
                    className="card-array__search-btn"
                    type="button"
                    aria-label="Update Items"
                    onClick={() => handleOpenSidePanel('items', 'Items')}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="darkorchid">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    <span className="card-array__badge">{(character.itemsDisplayed || character.items || []).length}</span>
                  </button>
                  <h3 className="card-array__title">Items</h3>
                </div>
                <div className="equipment-category">
                  <ul className="button-list">
                    {(character.itemsDisplayed || character.items || []).map(item => {
                      console.log('🔄 Rendering item:', item);
                      return (
                        <li key={item._id || `item-${item.name}`}>
                          <Link className="nav__el" to={`/element/${item.slug}`}>
                            {item.name || 'Unnamed Item'}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>

        {/* Threads Tab */}
        <div className={`tab-content ${activeTab === 'threads' ? '' : 'hidden'}`} id="tab-threads">
            <h4 className="card__sub-heading">Threads</h4>
            <p className="card__text">Threads functionality coming soon...</p>
          </div>
      </div>

      <div className="cardChar__footer">
        <Link className="btn btn--green btn--full details-btn" to={`/character/${character.slug}`}>
          Details
        </Link>
      </div>
    </div>

    {/* Confirm Modal */}
    {modalState && (
      <ConfirmModal
        isOpen={modalState.isOpen}
        message={modalState.message}
        type={modalState.type}
        okText={modalState.okText}
        cancelText={modalState.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleModalCancel}
      />
    )}

    {/* Upload Image Modal */}
    <UploadImageModal
      isOpen={showUploadModal}
      characterName={character.name}
      initialFile={droppedFile || undefined}
      onUpload={handleImageUpload}
      onCancel={() => {
        setShowUploadModal(false);
        setDroppedFile(null);
      }}
    />

    {/* Side Panel */}
    <SidePanel
      isOpen={showSidePanel}
      onClose={handleSidePanelClose}
      entity={character}
      field={sidePanelField}
      title={sidePanelTitle}
      onUpdate={(updatedEntity) => onUpdate(updatedEntity as Character)}
    />

    {/* Text Edit Modal */}
    {showTextEditModal && textEditData && (
      <div className="modal-overlay" onClick={handleTextEditModalClose}>
        <div className="text-edit-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Edit {textEditData.title}</h3>
            <button 
              className="modal-close-btn"
              onClick={handleTextEditModalClose}
            >
              ×
            </button>
          </div>
          
          <div className="modal-content">
            <div className="modal-layout">
              {/* Template Column - 1/5 width */}
              <div className="modal-template-column">
                <div className="template-column-header">
                  <h4 className="template-column-title">Templates</h4>
                  <button
                    className="create-template-btn"
                    onClick={() => setShowCreateTemplateModal(true)}
                    title="Create new template"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <use xlinkHref="/img/icons.svg#icon-plus" />
                    </svg>
                  </button>
                </div>
                <div className="template-list">
                  {/* Custom Templates - Always First */}
                  {(() => {
                    const fieldKey = textEditData.field === 'features' || textEditData.field === 'attributes' || textEditData.field === 'summary'
                      ? textEditData.field 
                      : 'features';
                    const fieldTemplates = Array.isArray(customTemplates[fieldKey]) ? customTemplates[fieldKey] : [];
                    
                    return fieldTemplates.map((template) => (
                      <div key={`custom-${template.name}`} className="custom-template-item">
                        <button
                          className="template-sidebar-option custom-template"
                          onClick={() => handleModalTemplateSelect(template)}
                        >
                          <span className="template-sidebar-name">{template.name}</span>
                          <span className="template-sidebar-preview">{template.content.split('\n').slice(0, 2).join(', ')}...</span>
                        </button>
                        <button
                          className="delete-template-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomTemplate(fieldKey, template.name);
                          }}
                          title="Delete template"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <use xlinkHref="/img/icons.svg#icon-trash" />
                          </svg>
                        </button>
                      </div>
                    ));
                  })()}
                  
                  {/* Built-in Templates - Always Second */}
                  {(textEditData.field === 'features' ? featuresTemplates : 
                    textEditData.field === 'attributes' ? attributesTemplates :
                    textEditData.field === 'summary' ? summaryTemplates :
                    featuresTemplates).map((template) => (
                    <button
                      key={`builtin-${template.name}`}
                      className="template-sidebar-option"
                      onClick={() => handleModalTemplateSelect(template)}
                    >
                      <span className="template-sidebar-name">{template.name}</span>
                      <span className="template-sidebar-preview">{template.content.split('\n').slice(0, 2).join(', ')}...</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Textarea Column - 4/5 width */}
              <div className="modal-textarea-column">
                <textarea
                  className="text-edit-textarea"
                  value={textEditData.content}
                  onChange={(e) => setTextEditData({
                    ...textEditData,
                    content: e.target.value
                  })}
                  placeholder={`Enter ${textEditData.title.toLowerCase()}...`}
                  autoFocus
                />
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button className="btn btn--gray" onClick={handleTextEditModalClose}>
              Cancel
            </button>
            <button 
              className="btn btn--gold" 
              onClick={handleTextEditSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Create Template Modal */}
    {showCreateTemplateModal && textEditData && (
      <div className="modal-overlay" onClick={() => setShowCreateTemplateModal(false)}>
        <div className="create-template-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Create New Template</h3>
            <button 
              className="modal-close-btn"
              onClick={() => setShowCreateTemplateModal(false)}
            >
              ×
            </button>
          </div>
          
          <div className="modal-content">
            <CreateTemplateForm
              field={textEditData.field as 'features' | 'attributes' | 'summary'}
              currentContent={textEditData.content}
              onCreateTemplate={handleCreateTemplate}
              onCancel={() => setShowCreateTemplateModal(false)}
            />
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CharacterCard; 